import { type Arrow, type Board, dirOf, pack, rayOf } from "./board";
import { type Picture, PICTURES, type Sym, inHub, inSlice } from "./pictures";
import { Rng } from "./rng";

/**
 * تخته‌ی مشترک (docs/arrow-race.md): یک نقشِ بزرگ (pictures.ts)، هر برشش به رنگِ یک بازیکن. هر کس
 * فقط فلش‌های رنگِ خودش را می‌زند و اولین کسی که رنگش را خالی کند می‌برد.
 *
 * فلش‌های دو رنگ هرگز جلوی هم را نمی‌گیرند (کاربر، ۷ آبان): نوکِ هر فلش فقط رو به جایی است که راهش تا
 * لبه از برشِ خودش یا جای خالی می‌گذرد — نه از نقشِ وسط و نه از برشِ دیگری.
 *
 * انصاف از قرینه می‌آید: فقط برشِ بازیکنِ اول ساخته می‌شود و برش‌های دیگر چرخش یا آینه‌ی همان‌اند.
 * با ۲ بازیکن روی نقشِ چهاربرشی، هر کس دو برشِ کنارِ هم دارد و سهمِ یکی چرخش یا آینه‌ی سهمِ دیگری
 * است. خودِ قاعده (هیچ راهی از برشِ دیگری نمی‌گذرد) هم با همین قرینه جابه‌جا می‌شود، پس پازلِ هر کس
 * عینِ پازلِ بقیه است، فقط چرخیده یا آینه‌شده.
 */

/** یک تخته‌ی مشترک: کدام نقش، چند فلش برای هر بازیکن، و بیشترین درازای فلش. */
export interface Spec {
  pic: Picture;
  /** فلشِ هر بازیکن (تیمی: هر هم‌تیمی، به‌طورِ میانگین). */
  arrows: number;
  len: number;
  /** احتمالِ پیچیدنِ فلش در هر گام؛ بیشتر = فلش‌های پرپیچ‌تر و گره‌های بیشتر. نبود = ۰٫۴. */
  turn?: number;
  /**
   * تیمی (۲ به ۲، صندلیِ ۰ و ۲ در برابرِ ۱ و ۳): نیمه‌ی هر تیم یک برش است و فلش‌های دو هم‌تیمی لای هم — راهِ
   * فلشِ هم‌تیمی ممکن است از فلشِ تو بگذرد (همکاری)، ولی هرگز از فلشِ تیمِ دیگر (کاربر، ۷ آبان).
   */
  teams?: boolean;
}

/** تخته‌ی مشترکِ بی مرحله (آزمون‌ها و نشانیِ کهنه): چهار بازو گرداگردِ شمسه. */
export const DEFAULT_SPEC: Spec = { pic: PICTURES.plus, arrows: 30, len: 6 };

const REGIONS: Record<Sym, number> = { rot4: 4, mir4: 4, mir2: 2, rot2: 2 };

/**
 * چه نقشی با چند نفر بازی می‌شود: ۲ نفر روی هر نقشی؛ ۳ و ۴ نفر فقط روی نقشِ چهاربرشی (با ۳ نفر برشِ چهارم
 * خالی می‌ماند)؛ تیمیِ ۲ به ۲ روی هر نقشی (هر تیم نیمی از برش‌ها).
 */
export function playable(pic: Picture, players: number, teams = false): boolean {
  if (teams) return players === 4;
  return players === 2 || ((players === 3 || players === 4) && REGIONS[pic.sym] === 4);
}

/** چند برش مالِ هر بازیکن (تیمی: هر تیم) است. */
const perPlayer = (pic: Picture, players: number, teams = false) => (players === 2 || teams ? REGIONS[pic.sym] / 2 : 1);

/** خانه‌های هر فلش، به‌طورِ میانگین: راه‌رفتنِ تصادفی کوتاه‌تر از بیشترین درازا تمام می‌شود. */
const cellsPerArrow = (len: number) => 0.9 * (2 + len) / 2;

/**
 * چیدمانِ نقش روی تخته‌ی n×n: reg[خانه] = شماره‌ی برش، −۱ بیرون، −۲ نقشِ وسط. n آن‌قدر بزرگ می‌شود
 * که برشِ اول دست‌کم cells خانه داشته باشد — پس نقش با هر اندازه‌ای همان نقش است، فقط ریزتر یا درشت‌تر.
 */
export interface Layout {
  n: number;
  reg: Int8Array;
  /** برشِ k: خانه‌ی برشِ اول ← خانه‌ی همتایش در برشِ k. */
  maps: ((c: number) => number)[];
  hub?: Board["hub"];
}

export function layout(pic: Picture, cells: number): Layout {
  // چرخش‌ها وسطِ خانه دارند (n فرد)، آینه‌ها خطِ وسط میانِ دو خانه (n زوج) تا خانه‌ای روی خطِ آینه نماند.
  const odd = pic.sym === "rot4" || pic.sym === "rot2";
  let n = odd ? 9 : 8;
  for (; n < 101; n += 2) if (part(pic, n).length >= cells) break;
  const own = part(pic, n);
  const xy = (c: number) => [c % n, Math.floor(c / n)] as const;
  const at = (x: number, y: number) => y * n + x;
  const rot = (c: number) => { const [x, y] = xy(c); return at(n - 1 - y, x); };
  const lr = (c: number) => { const [x, y] = xy(c); return at(n - 1 - x, y); };
  const tb = (c: number) => { const [x, y] = xy(c); return at(x, n - 1 - y); };
  const id = (c: number) => c;
  const maps = pic.sym === "rot4" ? [id, rot, (c: number) => rot(rot(c)), (c: number) => rot(rot(rot(c)))]
    : pic.sym === "mir4" ? [id, lr, tb, (c: number) => lr(tb(c))]
    : pic.sym === "mir2" ? [id, lr]
    : [id, (c: number) => rot(rot(c))];

  const reg = new Int8Array(n * n).fill(-1);
  const half = n / 2;
  if (pic.hub) {
    for (let c = 0; c < n * n; c++) {
      const [x, y] = xy(c);
      if (inHub(pic.hub, (x + 0.5 - half) / half, (y + 0.5 - half) / half)) reg[c] = -2;
    }
  }
  maps.forEach((m, k) => { for (const c of own) reg[m(c)] = k; });
  const hub = pic.hub && {
    x: half - pic.hub.rx * half, y: half - pic.hub.ry * half, w: 2 * pic.hub.rx * half, h: 2 * pic.hub.ry * half, kind: pic.hub.kind,
  };
  return { n, reg, maps, hub };
}

/** خانه‌های برشِ اول روی تخته‌ی n×n. */
function part(pic: Picture, n: number): number[] {
  const out: number[] = [];
  const m = (n - 1) / 2, half = n / 2;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = x - m, dy = y - m, u = dx / half, v = dy / half;
      if (inSlice(pic.sym, dx, dy) && !(pic.hub && inHub(pic.hub, u, v)) && pic.part(u, v)) out.push(y * n + x);
    }
  }
  return out;
}

/** چیدمانِ همان تخته‌ای که generateShared می‌سازد؛ per = چند برش مالِ هر بازیکن است. */
export function specLayout(spec: Spec, players: number): Layout & { per: number } {
  if (!playable(spec.pic, players, spec.teams)) throw new Error(`${spec.pic.id} is not for ${players} players`);
  const per = perPlayer(spec.pic, players, spec.teams);
  // تیمی: برشِ اول جای فلش‌های دو هم‌تیمی است
  const share = spec.teams ? 2 * spec.arrows : spec.arrows;
  return { ...layout(spec.pic, Math.round((share / per) * cellsPerArrow(spec.len))), per };
}

export function generateShared(seed: number, players: number, spec: Spec = DEFAULT_SPEC): Board {
  const L = specLayout(spec, players);
  const { n, reg, per } = L;
  const rng = new Rng(seed);
  const want = (spec.teams ? 2 * spec.arrows : spec.arrows) / per;

  // نوکِ فلشِ برشِ اول فقط رو به جایی که راهش از برشِ خودش یا بیرون بگذرد.
  const allow = (cells: readonly number[]) => rayOf(n, n, cells, dirOf(n, cells)).every((c) => reg[c] === -1 || reg[c] === 0);
  const ok = Array.from(reg, (r) => r === 0);
  const tier = { w: n, h: n, minLen: 2, maxLen: spec.len, fill: 0.93, turn: spec.turn ?? 0.4 };
  let one = pack(tier, ok, rng, allow);
  // گره‌گشایی گاهی فلش زیاد برمی‌دارد؛ آن وقت چند بار دیگر، و پُرفلش‌ترین.
  for (let i = 0; i < 6 && one.arrows.length < want * 0.85; i++) {
    const again = pack(tier, ok, rng, allow);
    if (again.arrows.length > one.arrows.length) one = again;
  }

  const arrows: Arrow[] = [];
  // تیمی: هر فلشِ برشِ اول مالِ یکی از دو هم‌تیمی، با نقشی که از تخته قرعه می‌شود — نوار، حلقه، شطرنجی یا
  // درهم. برش‌های تیمِ دیگر همان نقش را چرخیده یا آینه‌شده دارند، پس انصاف میانِ دو تیم سرِ جایش است.
  const slot = spec.teams ? teamSlots(one.arrows, n, rng.next(4)) : null;
  L.maps.forEach((m, k) => {
    const team = Math.floor(k / per);
    if (team >= players) return; // سه نفر روی نقشِ چهاربرشی: برشِ چهارم بی‌صاحب و خالی
    one.arrows.forEach((a, i) => {
      arrows.push({ id: arrows.length, cells: a.cells.map(m), owner: slot ? team + 2 * slot[i]! : team });
    });
  });
  return { w: n, h: n, arrows, hub: L.hub };
}

/**
 * تخته‌ی تک‌نفره‌ی مرحله‌های آفلاین (کاربر، ۷ آبان: «آفلاین مرحله‌ای و تک‌رنگ»): کلِ نقش فلش‌های خودِ بازیکن است.
 * رنگِ دیگری نیست که قیدِ «رو به رنگِ دیگر نه» بخواهد، پس نوکِ فلش به هر سو می‌تواند باشد و پازل درهم‌تر از سهمِ
 * یک برش است. نقش همان نقش است: همه‌ی برش‌های layout، با همان نقشِ وسط. spec.arrows کلِ فلش‌های تخته است.
 * فلش‌ها صاحب ندارند (owner نیست)، پس تخته با همان رنگِ ساده‌ی تیره کشیده می‌شود.
 */
/** چیدمانِ همان تخته‌ای که generateSolo می‌سازد (نقشِ کوچکِ مرحله‌ها). */
export function soloLayout(spec: Spec): Layout {
  return layout(spec.pic, Math.round((spec.arrows / REGIONS[spec.pic.sym]) * cellsPerArrow(spec.len)));
}

export function generateSolo(seed: number, spec: Spec): Board {
  const L = soloLayout(spec);
  const { n, reg } = L;
  const rng = new Rng(seed);
  const ok = Array.from(reg, (r) => r >= 0);
  const tier = { w: n, h: n, minLen: 2, maxLen: spec.len, fill: 0.93, turn: spec.turn ?? 0.4 };
  let b = pack(tier, ok, rng, () => true);
  for (let i = 0; i < 6 && b.arrows.length < spec.arrows * 0.85; i++) {
    const again = pack(tier, ok, rng, () => true);
    if (again.arrows.length > b.arrows.length) b = again;
  }
  return { w: n, h: n, arrows: b.arrows, hub: L.hub };
}

/**
 * هم‌تیمیِ هر فلش (۰ یا ۱) از روی خانه‌ی میانه‌اش: ۰ درهم (یکی در میان به ترتیبِ ساخت)، ۱ نوارهای افقیِ دوخانه‌ای،
 * ۲ حلقه‌های دوخانه‌ای گردِ وسط، ۳ شطرنجیِ سه‌خانه‌ای.
 */
function teamSlots(arrows: readonly { cells: number[] }[], n: number, kind: number): number[] {
  return arrows.map((a, i) => {
    const c = a.cells[Math.floor(a.cells.length / 2)]!;
    const x = c % n, y = Math.floor(c / n), m = (n - 1) / 2;
    if (kind === 0) return i % 2;
    if (kind === 1) return Math.floor(y / 2) % 2;
    if (kind === 2) return Math.floor(Math.max(Math.abs(x - m), Math.abs(y - m)) / 2) % 2;
    return (Math.floor(x / 3) + Math.floor(y / 3)) % 2;
  });
}

/** چهارگوشِ دورِ فلش‌های یک بازیکن، به واحدِ خانه — دوربین سرِ مسابقه روی همین می‌نشیند. */
export function ownerBox(b: Board, owner: number): { x: number; y: number; w: number; h: number } {
  let x0 = b.w, y0 = b.h, x1 = 0, y1 = 0;
  for (const a of b.arrows) {
    if (a.owner !== owner) continue;
    for (const c of a.cells) {
      const x = c % b.w, y = Math.floor(c / b.w);
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x + 1); y1 = Math.max(y1, y + 1);
    }
  }
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}
